import type { CoviToolStep } from "../model/types"

/**
 * Tool-visibility trace parsing + presentation.
 *
 * The completion response carries `tool_steps`: the ordered list of tools Sofia
 * ran that turn (research reads + named writes). `parseToolSteps` validates the
 * wire shape (defense in depth — the proxy already read it off the raw payload),
 * and the label/summary helpers turn low-cardinality tool names + statuses into
 * the product language the expandable CoviToolTrace disclosure shows.
 */

/** Hard cap so a pathological turn can't flood the transcript with chips. */
const MAX_TOOL_STEPS_PER_TURN = 24

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * Validate Sofia's raw `tool_steps` into the module-local wire type. Returns
 * null when absent/empty (the common tool-free turn) so the caller can skip
 * materializing chips. Unknown/oversized entries are dropped, not fatal — a
 * malformed step must never break the reply.
 */
export function parseToolSteps(value: unknown): CoviToolStep[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  const steps: CoviToolStep[] = []
  for (const entry of value) {
    if (steps.length >= MAX_TOOL_STEPS_PER_TURN) {
      break
    }
    if (!isRecord(entry)) {
      continue
    }
    const { name, status, is_research, is_error } = entry
    if (typeof name !== "string" || name.trim().length === 0) {
      continue
    }
    steps.push({
      name: name.trim(),
      status: typeof status === "string" ? status : "unknown",
      // Default research=true (mirrors the backend default), so a new read tool
      // surfaces as research even before the FE knows its name. Only an explicit
      // false marks a write/artifact tool.
      is_research: is_research !== false,
      is_error: is_error === true,
    })
  }
  return steps.length > 0 ? steps : null
}

/**
 * Friendly labels for the tool names Sofia reports. Kept in sync with the
 * Sofia tool builders in sofia-agent (`product/org_data.py`,
 * `product/knowledge.py`, `product/memory.py`, `product/metric_catalog_tools.py`,
 * `product/actions.py`). An unknown name falls back to a de-underscored title,
 * so a new backend tool still renders a readable chip without a FE change.
 */
const TOOL_LABELS: Record<string, string> = {
  // org_data.py — reads over the user's runs / simulations / monitoring / resources
  get_runs_overview: "Reviewing your runs",
  list_recent_runs: "Looking up recent runs",
  get_run: "Reading a run",
  list_run_simulations: "Reading run results",
  get_simulation_output: "Reading a transcript",
  list_monitoring_conversations: "Checking monitoring",
  get_conversation: "Reading a conversation",
  get_conversation_metrics: "Reading conversation metrics",
  get_metric_breakdown: "Analyzing a metric",
  list_org_resources: "Looking up your resources",
  // knowledge.py — the product knowledge corpus
  search_playbooks: "Searching the playbooks",
  read_playbook: "Reading a playbook",
  recall_failure_patterns: "Recalling failure patterns",
  vertical_guidance: "Checking vertical guidance",
  // metric_catalog_tools.py
  describe_metric: "Reading metric details",
  // memory.py — durable org/user memory
  recall_memory: "Recalling what you told me",
  save_memory: "Saving to memory",
  // actions.py — the write-proposal + visualization tools
  propose_org_action: "Preparing an action",
  render_visualization: "Drawing a chart",
}

export function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, " ")
}

/**
 * Human-readable outcome for one tool step, shown as a small trailing note in
 * the expandable "what Sofia did" trace. The wire `status` is a
 * low-cardinality token from the model tool chokepoint
 * (`succeeded`/`failed`/`deduped`/`unavailable`/`invalid_arguments`/`unknown`);
 * this maps the ones worth surfacing to plain language. `succeeded` returns null
 * — a bare completed step needs no "succeeded" tag (the check icon says it).
 */
const TOOL_STATUS_LABELS: Record<string, string> = {
  failed: "failed",
  unavailable: "unavailable",
  invalid_arguments: "bad arguments",
  deduped: "cached",
}

export function toolStatusLabel(status: string): string | null {
  return TOOL_STATUS_LABELS[status] ?? null
}

/**
 * One-line summary for the collapsed tool-trace header — "Researched N sources",
 * "Ran N tools", or a mix. Reads only the counts the completion payload carries
 * (research vs. write, error vs. ok), so it stays honest about what actually
 * happened without needing per-tool args or results, which the endpoint does not
 * send today. See the module docstring for the streaming scope.
 */
export function toolTraceSummary(
  steps: { isResearch: boolean; isError: boolean }[],
): string {
  const total = steps.length
  if (total === 0) {
    return "What Sofia did"
  }
  const errors = steps.filter((step) => step.isError).length
  const researchCount = steps.filter((step) => step.isResearch).length
  const writeCount = total - researchCount
  const parts: string[] = []
  if (researchCount > 0) {
    parts.push(
      researchCount === 1
        ? "Researched 1 source"
        : `Researched ${researchCount} sources`,
    )
  }
  if (writeCount > 0) {
    parts.push(
      writeCount === 1 ? "prepared 1 action" : `prepared ${writeCount} actions`,
    )
  }
  const base =
    parts.length > 0
      ? parts.join(", ")
      : `Ran ${total} ${total === 1 ? "tool" : "tools"}`
  return errors > 0 ? `${base} (${errors} failed)` : base
}
